/**
 * Orders HUD (Command Center)
 *
 * Main component for the tactical Orders Command Center.
 * Supports List and Kanban views with clickable KPI filters.
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  List,
  useListContext,
  useRefresh,
} from 'react-admin';
import ScrollTopPagination from '../shared/ScrollTopPagination';
import { useSocketIO } from '../../providers/SocketIOProvider';
import { HUDProvider, useHUD } from './context/HUDContext';
import type { Order } from './context/HUDContext';
import { useHUDTheme } from './theme';
import { useFilterPatch } from './useFilterPatch';
import HUDHeader from './components/HUDHeader';
import RightNow from './components/RightNow';
import NeedsALook from './components/NeedsALook';
import { Band } from '../reskin/primitives';
import ThreatSummary from './components/ThreatSummary';
import IntelligencePanel from './components/IntelligencePanel';
import OrdersTable from './components/OrdersTable';
import OrdersFilterBar from './components/OrdersFilterBar';
import OrderPreviewDrawer from './components/OrderPreviewDrawer';
import { useEffectiveStore, storeFilterFor } from '../../context/StoreScopeContext';
import { SHOW_SLA } from '../../config/features';

// =============================================================================
// HUD Order Listener (refreshes the list + triggers sound animation when mounted)
// =============================================================================

export function HUDOrderListener() {
  const { triggerSoundAnimation } = useHUD();
  const { onNewOrder, onOrderStatusChanged } = useSocketIO();
  const refresh = useRefresh();
  const statusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return onNewOrder(() => {
      triggerSoundAnimation();
      refresh(); // Refresh the React-Admin list to show the new order
    });
  }, [onNewOrder, triggerSoundAnimation, refresh]);

  // An order's status changed (courier, app, another admin): reload the list quietly so
  // the status chips are current without a page refresh. Debounced — a burst of changes
  // reloads once.
  useEffect(() => {
    const off = onOrderStatusChanged(() => {
      if (statusTimer.current) clearTimeout(statusTimer.current);
      statusTimer.current = setTimeout(() => refresh(), 500);
    });
    return () => {
      off();
      if (statusTimer.current) clearTimeout(statusTimer.current);
    };
  }, [onOrderStatusChanged, refresh]);

  return null;
}

// =============================================================================
// Status Filter Controller (sets react-admin filters from KPI clicks)
// =============================================================================

function StatusFilterController({
  statusFilter,
}: {
  statusFilter: string | null;
}) {
  // Patch rather than replace — the filter bar owns search and the date range,
  // and rebuilding the whole filter object from a stale copy used to drop them.
  const patchFilters = useFilterPatch();

  useEffect(() => {
    patchFilters({ status: statusFilter ?? undefined });
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// =============================================================================
// HUD Content (inside List context)
// =============================================================================

/** Kanban and the card grid were both removed — Orders Centre is the table. */

function HUDContent() {
  const { hud } = useHUDTheme();
  const { data, isLoading, total, isFetching, sort } = useListContext<Order>();

  // Stamp for the header — when the list last came back from the API.
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  useEffect(() => {
    if (!isFetching && data) setUpdatedAt(new Date());
  }, [isFetching, data]);
  const {
    orderThreats,
    orderPriorities,
    insights,
    stats,
    threatSummary,
    highlightedOrderIds,
    setHighlightedOrderIds,
    computeAnalytics,
    isSoundPlaying,
  } = useHUD();

  // Orders Centre is the table, full stop. The card grid and its switcher were
  // removed — cards were never used, and the stored `ordersHud.viewMode`
  // preference is deliberately left alone so an old saved 'cards' can't resurrect
  // a view that no longer renders.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [previewOrder, setPreviewOrder] = useState<Order | null>(null);

  // Compute analytics when data changes
  useEffect(() => {
    if (data && !isLoading) {
      computeAnalytics(data as Order[]);
    }
  }, [data, isLoading, computeAnalytics]);

  // Re-compute every 30 seconds for threat updates
  useEffect(() => {
    if (!data || isLoading) return;

    const interval = setInterval(() => {
      computeAnalytics(data as Order[]);
    }, 30000);

    return () => clearInterval(interval);
  }, [data, isLoading, computeAnalytics]);

  const handleHighlightOrders = useCallback(
    (orderIds: string[]) => {
      setHighlightedOrderIds(new Set(orderIds));
      setTimeout(() => setHighlightedOrderIds(new Set()), 5000);
    },
    [setHighlightedOrderIds]
  );

  // Map insight types to status filters
  const insightStatusMap: Record<string, string> = {
    bottleneck: 'placed',
    // 'driver-shortage' intentionally omitted — that insight is hidden.
  };

  const handleInsightFilter = useCallback(
    (insightType: string, orderIds: string[]) => {
      const mappedStatus = insightStatusMap[insightType];
      if (mappedStatus) {
        setStatusFilter((prev) => (prev === mappedStatus ? null : mappedStatus));
      }
      setHighlightedOrderIds(new Set(orderIds));
      setTimeout(() => setHighlightedOrderIds(new Set()), 5000);
    },
    [setHighlightedOrderIds]
  );

  // Branch scope is applied server-side (the List's permanent `storeId` filter,
  // see OrdersHUD below), as are search and status (OrdersFilterBar) — so
  // nothing is re-filtered here; doing so would only filter the loaded page.
  const filteredOrders = (data || []) as Order[];

  const header = (
    <HUDHeader
      isSoundPlaying={isSoundPlaying}
      total={typeof total === 'number' ? total : undefined}
      updatedAt={updatedAt}
    />
  );

  if (isLoading) {
    return (
      <>
        {header}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress sx={{ color: hud.accent }} />
          <Typography sx={{ mt: 2, color: hud.textSecondary }}>
            Loading orders…
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <HUDOrderListener />
      <StatusFilterController statusFilter={statusFilter} />

      {header}

      <Box sx={{ px: 2.75, pb: 4 }}>
      <RightNow />

      {/* Stuck-order cards, bucketed server-side; a click flashes the rows. */}
      <NeedsALook onHighlight={handleHighlightOrders} />

      {/* Threat (SLA) indicators — hidden while SHOW_SLA is off */}
      {SHOW_SLA && (
        <Box sx={{ px: 0.5, pt: 1, pb: 0.5 }}>
          <ThreatSummary summary={threatSummary} />
        </Box>
      )}

      {/* KPI blocks, the search/store-badge toolbar and the kanban view were all
          removed: Orders Centre is a table + cards, filtered from OrdersFilterBar. */}

      <IntelligencePanel
        insights={insights}
        onHighlightOrders={handleHighlightOrders}
        onInsightFilter={handleInsightFilter}
      />

      <Band note={sort?.field === 'createdAt' && sort?.order === 'DESC' ? 'newest first' : undefined}>All orders</Band>
      <OrdersFilterBar />

      <OrdersTable
        orders={filteredOrders}
        highlightedOrderIds={highlightedOrderIds}
        onView={setPreviewOrder}
      />
      </Box>
      <OrderPreviewDrawer order={previewOrder} onClose={() => setPreviewOrder(null)} />
    </>
  );
}

// =============================================================================
// Pagination
// =============================================================================

const HUDPagination = () => {
  const { hud } = useHUDTheme();
  return (
  <ScrollTopPagination
    rowsPerPageOptions={[25, 50, 100, 200]}
    sx={{
      '& .MuiToolbar-root': {
        bgcolor: hud.elevated,
        color: hud.textPrimary,
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        color: hud.textSecondary,
      },
      '& .MuiSelect-select': {
        color: hud.textPrimary,
      },
      '& .MuiIconButton-root': {
        color: hud.textSecondary,
        '&:hover': { bgcolor: hud.hover },
        '&.Mui-disabled': { color: hud.textMuted },
      },
    }}
  />
  );
};

// =============================================================================
// Main Component
// =============================================================================

export function OrdersHUD(props: any) {
  const { hud } = useHUDTheme();
  // The app-bar picker's store, by id (externalId). Untouched, Orders defaults
  // to All stores; "Select" also means no branch filter here — only Live Orders
  // gates on a pick. A permanent filter, so it never lands in the filter bar
  // or the URL.
  const storeId = storeFilterFor(useEffectiveStore());
  const storeFilter = React.useMemo(() => (storeId ? { storeId } : undefined), [storeId]);

  return (
    <HUDProvider>
      <List
        {...props}
        filter={storeFilter}
        title="Orders"
        actions={false}
        // react-admin's default empty page would replace the whole layout with
        // "No Orders yet." on an environment with no orders (staging, day one on
        // prod) — the header, tiles and filters should still be there.
        empty={false}
        pagination={<HUDPagination />}
        perPage={50}
        sort={{ field: 'createdAt', order: 'DESC' }}
        sx={{
          bgcolor: hud.background,
          minHeight: '100vh',
          // react-admin gives the list a 15px top margin and a raised paper; the
          // page header wants to sit flush under the app bar like the Dashboard's.
          '& .RaList-main': { mt: 0 },
          '& .RaList-content': {
            bgcolor: 'transparent',
            boxShadow: 'none',
            borderRadius: 0,
          },
        }}
      >
        <HUDContent />
      </List>
    </HUDProvider>
  );
}

export default OrdersHUD;
